/**
 * Insurance checkout variant configuration
 *
 * The three insurance checkout screens (`invoice`, `without_abtretung_patient`,
 * `without_abtretung_physiofit`) share all logic and rendering; they only differ in the
 * onboarding step key, the billing consent copy, and the reimbursement disclaimer text.
 * Those differences are captured here and consumed by `useInsuranceCheckoutScreen`.
 *
 * The variant string doubles as the `InsuranceBillingType` used for the explain-route guard
 * and back navigation, so it can be passed straight to `useInsuranceExplainBillingGuard` and
 * `getInsuranceExplainPath`.
 */

import type { WebAppOnboardingCurrentStep } from '@physiofit-digital/physiofit_types';

export type InsuranceCheckoutVariant = 'invoice' | 'without_abtretung_patient' | 'without_abtretung_physiofit';

export type InsuranceCheckoutBillingMath = {
  refundRate: number;
  courseAmount: number;
  reimbursement: number;
  subsidy: number;
};

export type InsuranceCheckoutNames = {
  insuranceName: string;
  insuranceShortName: string;
};

export type InsuranceCheckoutVariantConfig = {
  onboardingStep: WebAppOnboardingCurrentStep;
  billingCheckboxText: string;
  buildDisclaimerText: (math: InsuranceCheckoutBillingMath, names: InsuranceCheckoutNames) => string;
};

function buildInvoiceDisclaimerText(
  { refundRate, courseAmount, reimbursement, subsidy }: InsuranceCheckoutBillingMath,
  { insuranceShortName }: InsuranceCheckoutNames,
): string {
  if (refundRate < 1) {
    return `Ablauf: Nach Kursabschluss erhältst du eine Rechnung über ${courseAmount} € von uns. Sobald sie bezahlt ist, reichen wir deine Unterlagen bei der ${insuranceShortName} ein. Im Anschluss erhältst du von uns ${subsidy} € als Förderung und von der ${insuranceShortName} ${reimbursement} €. Sollte die ${insuranceShortName} die Gebühr nicht bezahlen, erstatten wir sie dir.`;
  }
  return `Ablauf: Nach Kursabschluss erhältst du eine Rechnung über ${courseAmount} € von uns. Sobald sie bezahlt ist, reichen wir deine Unterlagen bei der ${insuranceShortName} ein. Im Anschluss erhältst du von der ${insuranceShortName} ${reimbursement} € als Erstattung. Sollte die ${insuranceShortName} die Gebühr nicht bezahlen, erstatten wir sie dir.`;
}

function buildWithoutAbtretungPatientDisclaimerText(
  { refundRate, reimbursement, subsidy }: InsuranceCheckoutBillingMath,
  { insuranceName, insuranceShortName }: InsuranceCheckoutNames,
): string {
  if (refundRate < 1) {
    return `Ablauf: Nach Kursabschluss reichen wir deine Unterlagen direkt bei der ${insuranceName} ein. Du erhältst von der ${insuranceShortName} ${reimbursement} €. Den Erstattungsbetrag leitest du anschließend an uns weiter. Wir übernehmen in dem Fall ${subsidy} € für dich als Förderung.`;
  }
  return `Ablauf: Nach Kursabschluss reichen wir deine Unterlagen direkt bei der ${insuranceName} ein. Die ${insuranceShortName} überweist dir ${reimbursement} €, die du anschließend an uns weiterleitest.`;
}

function buildWithoutAbtretungPhysiofitDisclaimerText(
  _math: InsuranceCheckoutBillingMath,
  { insuranceName, insuranceShortName }: InsuranceCheckoutNames,
): string {
  return `Ablauf: Nach Kursabschluss reichen wir deine Unterlagen direkt bei der ${insuranceName} ein. In der Regel erstattet die ${insuranceShortName} die Kursgebühr direkt an uns. In seltenen Fällen kann es auch zu einer Überweisung an dich kommen. In diesem Fall leitest du die Erstattung einfach an uns weiter. Auf dich kommen zu keinem Zeitpunkt Kosten zu.`;
}

export const INSURANCE_CHECKOUT_VARIANTS: Record<InsuranceCheckoutVariant, InsuranceCheckoutVariantConfig> = {
  invoice: {
    onboardingStep: 'insurance_invoice_checkout',
    billingCheckboxText:
      'Ich beauftrage Physiofit, nach Kursabschluss meine Erstattung mit meiner Krankenkasse abzuwickeln.',
    buildDisclaimerText: buildInvoiceDisclaimerText,
  },
  without_abtretung_patient: {
    onboardingStep: 'insurance_without_abtretung_patient_checkout',
    billingCheckboxText:
      'Ich beauftrage Physiofit, nach Kursabschluss meine Unterlagen bei meiner Krankenkasse einzureichen, und leite die erhaltene Erstattung an Physiofit weiter.',
    buildDisclaimerText: buildWithoutAbtretungPatientDisclaimerText,
  },
  without_abtretung_physiofit: {
    onboardingStep: 'insurance_without_abtretung_physiofit_checkout',
    billingCheckboxText:
      'Ich beauftrage Physiofit, nach Kursabschluss meine Unterlagen bei meiner Krankenkasse einzureichen.',
    buildDisclaimerText: buildWithoutAbtretungPhysiofitDisclaimerText,
  },
};
